import CheckIcon from '@mui/icons-material/Check';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Copy as CopyIcon } from '@vy/ui-design-system';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Column, Row, VisualizationLink } from './types';
import {
  copilotTableContainer,
  copilotTableTitleRow,
  copilotTableTitle,
  copilotTableHeaderCell,
  copilotTableBodyCell,
} from './styles';
import { CopilotVisualizationLink } from './CopilotVisualizationLink';

type CopilotDataTableProps = {
  title?: string;
  columns: Column[];
  rows: Row[];
  link?: VisualizationLink;
};

function getSentimentColor(value: string | number): string | undefined {
  const str = String(value).trim();
  if (str.startsWith('+')) return '#42ae5c';
  if (str.startsWith('-')) return '#ee2049';
  return undefined;
}

function LinkedCell({ value, href }: { value: string; href: string }) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(href);
  };

  return (
    <Box
      component="span"
      onClick={handleClick}
      sx={{
        color: '#6282FF',
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      {value}
    </Box>
  );
}

type ToolbarIconsProps = {
  copied: boolean;
  onCopy: () => void;
  onExport: () => void;
};

function ToolbarIcons({ copied, onCopy, onExport }: ToolbarIconsProps) {
  return (
    <Box sx={{ display: 'flex', gap: 0.25 }}>
      <Tooltip title={copied ? 'Copied!' : 'Copy table'} placement="top">
        <IconButton onClick={onCopy} size="small" sx={{ color: '#7e8796', p: 0.25, '&:hover': { color: '#6282FF' } }}>
          {copied ? <CheckIcon sx={{ fontSize: 12 }} /> : <CopyIcon width={12} height={12} />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Download CSV" placement="top">
        <IconButton onClick={onExport} size="small" sx={{ color: '#7e8796', p: 0.25, '&:hover': { color: '#6282FF' } }}>
          <FileDownloadOutlinedIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export function CopilotDataTable({ title, columns, rows, link }: CopilotDataTableProps) {
  const [copied, setCopied] = useState(false);

  if (rows.length === 0) return null;

  const handleCopy = async () => {
    try {
      const header = columns.map((col) => col.label).join('\t');
      const body = rows.map((row) => columns.map((col) => String(row[col.key] ?? '')).join('\t')).join('\n');
      await navigator.clipboard.writeText(`${header}\n${body}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable
    }
  };

  const handleExportCsv = () => {
    const escapeCsvField = (value: string) => `"${value.replace(/"/g, '""')}"`;
    const header = columns.map((col) => escapeCsvField(col.label)).join(',');
    const body = rows.map((row) => columns.map((col) => escapeCsvField(String(row[col.key] ?? ''))).join(',')).join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'vayu-copilot-table.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const linkKeyMap = new Map<string, string>();
  for (const col of columns) {
    if (col.linkKey) {
      linkKeyMap.set(col.key, col.linkKey);
    }
  }

  return (
    <Box>
      <Box sx={copilotTableTitleRow}>
        <Typography sx={copilotTableTitle}>{title ?? 'Details'}</Typography>
        <ToolbarIcons copied={copied} onCopy={handleCopy} onExport={handleExportCsv} />
      </Box>
      <Box sx={copilotTableContainer}>
        <MuiTable size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} align={col.align ?? 'left'} sx={copilotTableHeaderCell}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIdx) => (
              <TableRow key={rowIdx}>
                {columns.map((col) => {
                  const cellValue = row[col.key] ?? '';
                  const sentimentColor = col.sentiment ? getSentimentColor(cellValue) : undefined;
                  const isLast = rowIdx === rows.length - 1;
                  const linkKey = linkKeyMap.get(col.key);
                  const href = linkKey ? String(row[linkKey] ?? '') : '';

                  return (
                    <TableCell
                      key={col.key}
                      align={col.align ?? 'left'}
                      sx={{
                        ...copilotTableBodyCell,
                        ...(col.align === 'right' && { fontWeight: 500 }),
                        ...(sentimentColor && { color: sentimentColor, fontWeight: 500 }),
                        ...(isLast && { borderBottom: 'none' }),
                      }}
                    >
                      {href ? <LinkedCell value={String(cellValue)} href={href} /> : cellValue}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </Box>
      {link && <CopilotVisualizationLink {...link} />}
    </Box>
  );
}
